// PROMPT 149: ficha del cliente (boton "Info" del encabezado del chat). Panel a la derecha en la
// computadora y pantalla completa en el celular.
import { useState } from "react";
import { Check, Copy, ExternalLink, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar } from "@/components/agente/ChatWhatsApp";
import { EtiquetaChip } from "@/components/agente/EtiquetasWhatsApp";
import { SIN_NUMERO, nombreParaAvatar, nombreVisible } from "@/lib/marcasWa";
import { BloqueoEnFicha } from "@/components/agente/BloqueoCliente"; // PROMPT 150

const ESTADOS = { agente: "La atiende el agente", persona: "La atiende una persona", cerrada: "Archivada" };
const IDIOMAS = { es: "Español", en: "Inglés" };

function fechaDe(valor) {
  try {
    const d = new Date(valor);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "";
  }
}

function Dato({ titulo, children }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{titulo}</p>
      <div className="mt-0.5 text-sm text-slate-800">{children}</div>
    </div>
  );
}

function Nombre({ conversacion, onActualizar }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const id = conversacion.id;

  const empezar = () => {
    setValor(conversacion.nombre_editado || conversacion.nombre_contacto || conversacion.nombre_cliente || "");
    setEditando(true);
  };

  const guardar = async (nuevo) => {
    setOcupado(true);
    try {
      const { data } = await api.put(`/whatsapp/conversaciones/${encodeURIComponent(id)}/contacto`, {
        nombre_editado: nuevo,
      });
      onActualizar(id, { nombre_editado: data && data.nombre_editado ? data.nombre_editado : null });
      setEditando(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOcupado(false);
    }
  };

  if (editando) {
    return (
      <div className="flex items-center gap-1" data-testid="wa-ficha-nombre-edicion">
        <Input
          value={valor}
          maxLength={80}
          autoFocus
          disabled={ocupado}
          onChange={(e) => setValor(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") guardar(valor.trim() || null);
            if (e.key === "Escape") setEditando(false);
          }}
          aria-label="Nombre del cliente"
          className="h-8 min-w-0 flex-1"
          data-testid="wa-ficha-nombre-input"
        />
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => guardar(valor.trim() || null)} disabled={ocupado} aria-label="Guardar el nombre" data-testid="wa-ficha-nombre-guardar">
          <Check className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditando(false)} aria-label="Cancelar">
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-1">
        <span className="truncate text-base font-semibold text-slate-900" data-testid="wa-ficha-nombre">
          {nombreVisible(conversacion)}
        </span>
        <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={empezar} aria-label="Cambiar el nombre" data-testid="wa-ficha-nombre-editar">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </div>
      {conversacion.nombre_editado ? (
        <button type="button" onClick={() => guardar(null)} disabled={ocupado} className="text-xs text-slate-500 underline hover:text-slate-700" data-testid="wa-ficha-nombre-restablecer">
          Volver al nombre de WhatsApp
        </button>
      ) : null}
    </div>
  );
}

function Reservas({ conversacion, onActualizar }) {
  const [codigo, setCodigo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const reservas = conversacion.reservas || [];
  const base = `/whatsapp/conversaciones/${encodeURIComponent(conversacion.id)}/reservas`;

  const aplicar = (lista) =>
    onActualizar(conversacion.id, { reservas: lista, reserva_ids: lista.map((r) => r.id) });

  const vincular = async () => {
    const limpio = codigo.trim();
    if (!limpio) return;
    setOcupado(true);
    try {
      const { data } = await api.post(base, { codigo: limpio });
      aplicar((data && data.reservas) || []);
      setCodigo("");
      toast.success("Reserva vinculada.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOcupado(false);
    }
  };

  const desvincular = async (r) => {
    setOcupado(true);
    try {
      const { data } = await api.delete(`${base}/${encodeURIComponent(r.id)}`);
      aplicar((data && data.reservas) || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="wa-ficha-reservas">
      {reservas.length === 0 ? <p className="text-sm text-slate-500">No tiene reservas vinculadas.</p> : null}
      <ul className="space-y-2">
        {reservas.map((r) => (
          <li key={r.id} className="rounded-md border border-slate-200 p-2 text-sm" data-testid={`wa-ficha-reserva-${r.id}`}>
            <div className="flex items-center justify-between gap-2">
              <a
                href={`/reservas?q=${encodeURIComponent(r.codigo_booking || "")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:underline"
                data-testid={`wa-ficha-reserva-codigo-${r.id}`}
              >
                {r.codigo_booking || "Sin código"}
                <ExternalLink className="h-3 w-3" />
              </a>
              <button
                type="button"
                onClick={() => desvincular(r)}
                disabled={ocupado}
                title="Desvincular esta reserva"
                aria-label="Desvincular esta reserva"
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                data-testid={`wa-ficha-desvincular-${r.id}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-slate-700">{r.tour_nombre || "Tour sin nombre"}</p>
            <p className="text-xs text-slate-500">
              {[r.fecha, r.hora].filter(Boolean).join(" · ")}
              {r.estado ? ` · ${r.estado}` : ""}
            </p>
            {Number(r.saldo_pendiente) > 0 ? (
              <p className="text-xs font-medium text-amber-700">
                Saldo: {r.saldo_pendiente} {r.moneda || ""}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-1">
        <Input
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") vincular();
          }}
          placeholder="Código de la reserva"
          aria-label="Código de la reserva"
          className="h-8 min-w-0 flex-1"
          data-testid="wa-ficha-vincular-codigo"
        />
        <Button size="sm" variant="outline" onClick={vincular} disabled={ocupado || !codigo.trim()} data-testid="wa-ficha-vincular">
          Vincular reserva
        </Button>
      </div>
    </div>
  );
}

// PROMPT 151: "No mandarle difusiones" (lo anota todo el equipo; no bloquea el chat).
function DifusionesEnFicha({ conversacion, onActualizar }) {
  const [guardando, setGuardando] = useState(false);
  const sin = Boolean(conversacion.sin_difusiones);
  const cambiar = async (sinNuevo) => {
    setGuardando(true);
    try {
      await api.put(`/whatsapp/conversaciones/${encodeURIComponent(conversacion.id)}/difusiones`, {
        recibir: sinNuevo === false,
      });
      onActualizar(conversacion.id, { sin_difusiones: sinNuevo });
      toast.success(sinNuevo ? "No se le van a mandar difusiones." : "Vuelve a recibir difusiones.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };
  return (
    <label className="flex cursor-pointer items-start gap-2" data-testid="wa-ficha-sin-difusiones">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 accent-emerald-600"
        checked={sin}
        disabled={guardando}
        onChange={(e) => cambiar(e.target.checked)}
        data-testid="wa-ficha-sin-difusiones-check"
      />
      <span className="min-w-0">
        <span className="block text-sm text-slate-800">No mandarle difusiones</span>
        <span className="block text-[11px] text-slate-500">
          Queda afuera de las difusiones. Puede seguir escribiendo y se le contesta normal.
        </span>
      </span>
    </label>
  );
}

export default function FichaCliente({
  conversacion,
  marcaTexto,
  marcaColor,
  etiquetas,
  onCerrar,
  onActualizar,
  onAbrirConversacion,
  bloqueo,
}) {
  const c = conversacion;
  const ids = c.etiquetas || [];
  const puestas = ids.map((id) => (etiquetas || []).find((e) => e.id === id)).filter(Boolean);
  const notas = (c.mensajes || []).filter((m) => m.direccion === "interna" && !m.borrada).slice(-3).reverse();
  const anteriores = c.anteriores || [];

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(c.telefono);
      toast.success("Teléfono copiado.");
    } catch {
      toast.error("No se pudo copiar. Seleccionalo y copialo a mano.");
    }
  };

  return (
    <aside
      className="fixed inset-0 z-40 flex flex-col bg-white lg:static lg:inset-auto lg:z-auto lg:w-[340px] lg:shrink-0 lg:border-l lg:border-slate-200"
      aria-label="Info del cliente"
      data-testid="wa-ficha"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-3 py-3">
        <Button variant="ghost" size="icon" onClick={onCerrar} aria-label="Cerrar la ficha" className="h-9 w-9" data-testid="wa-ficha-cerrar">
          <X className="h-5 w-5" />
        </Button>
        <h2 className="font-heading text-base font-semibold text-slate-900">Info del cliente</h2>
      </div>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        <div className="flex items-center gap-3">
          <Avatar nombre={nombreParaAvatar(c)} telefono={c.telefono} className="h-14 w-14 text-lg" anillo={marcaColor} />
          <Nombre conversacion={c} onActualizar={onActualizar} />
        </div>
        <Dato titulo="Teléfono">
          <span className="flex items-center gap-1">
            <span data-testid="wa-ficha-telefono">{c.telefono || SIN_NUMERO}</span>
            {c.telefono ? (
              <button type="button" onClick={copiar} title="Copiar el teléfono" aria-label="Copiar el teléfono" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid="wa-ficha-copiar">
                <Copy className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </span>
        </Dato>
        {marcaTexto ? (
          <Dato titulo="Escribió por">
            <span className="inline-flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: marcaColor }} />
              {marcaTexto}
            </span>
          </Dato>
        ) : null}
        <div className="grid grid-cols-2 gap-4">
          <Dato titulo="Idioma">{c.idioma ? IDIOMAS[c.idioma] || c.idioma.toUpperCase() : "Sin definir"}</Dato>
          <Dato titulo="Estado">{ESTADOS[c.estado] || c.estado || "—"}</Dato>
        </div>
        <Dato titulo="Asignada a">{c.asignada_nombre || "Sin asignar"}</Dato>
        <Dato titulo="Etiquetas">
          {puestas.length ? (
            <span className="flex flex-wrap gap-1" data-testid="wa-ficha-etiquetas">
              {puestas.map((e) => (
                <EtiquetaChip key={e.id} etiqueta={e} />
              ))}
            </span>
          ) : (
            <span className="text-slate-500">Sin etiquetas. Se ponen desde “Etiquetas” en el encabezado del chat.</span>
          )}
        </Dato>
        <Dato titulo="Reservas vinculadas">
          <Reservas conversacion={c} onActualizar={onActualizar} />
        </Dato>
        <Dato titulo="Conversaciones anteriores">
          {anteriores.length === 0 ? (
            <span className="text-slate-500">No hay otras conversaciones de este cliente con esta marca.</span>
          ) : (
            <ul className="space-y-1" data-testid="wa-ficha-anteriores">
              {anteriores.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => onAbrirConversacion && onAbrirConversacion(a)}
                    className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                  >
                    {fechaDe(a.created_at) || "Sin fecha"} · {a.cantidad_de_mensajes || 0} mensajes
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Dato>
        <Dato titulo="Últimas notas del equipo">
          {notas.length === 0 ? (
            <span className="text-slate-500">Todavía no hay notas.</span>
          ) : (
            <ul className="space-y-1.5" data-testid="wa-ficha-notas">
              {notas.map((n) => (
                <li key={n.id} className="rounded-md bg-[#fff3b0] px-2 py-1.5 text-sm text-slate-800">
                  <p className="whitespace-pre-wrap break-words">{n.texto}</p>
                  <p className="mt-0.5 text-[11px] text-amber-800">
                    {n.usuario || "Alguien del equipo"} · {fechaDe(n.created_at)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Dato>
        {bloqueo ? (
          <Dato titulo="Bloqueo">
            <BloqueoEnFicha bloqueo={bloqueo} sinNumero={!c.telefono} />
          </Dato>
        ) : null}
        {c.telefono ? (
          <Dato titulo="Difusiones">
            <DifusionesEnFicha conversacion={c} onActualizar={onActualizar} />
          </Dato>
        ) : null}
      </div>
    </aside>
  );
}
